
/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Canopy is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Canopy is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Canopy, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Canopy:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Canopy broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of selected options
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Canopy utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Canopy only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Canopy.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Canopy is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Canopy provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
// CT - Product card carousel (delegated: funciona con infinite scroll / AJAX)
(function () {
  // Marca de carga (para que puedas verificar en consola)
  window.__CT_PC_LOADED = true;
  console.log("✅ CT PC custom.js cargó");

  function getIndex(root) {
    return parseInt(root.getAttribute("data-ct-pc-index") || "0", 10);
  }

  function setIndex(root, index) {
    root.setAttribute("data-ct-pc-index", String(index));
  }

  function getTotal(root) {
    const t = parseInt(root.getAttribute("data-ct-pc-total") || "0", 10);
    if (t) return t;
    return root.querySelectorAll(".ct-pc-slide").length;
  }

  function goTo(root, nextIndex) {
    const track = root.querySelector(".ct-pc-track");
    if (!track) return;

    const total = getTotal(root);
    if (total <= 1) return;

    const index = (nextIndex + total) % total;
    track.style.transform = `translateX(-${index * 100}%)`;
    setIndex(root, index);
  }

  // Clicks delegados (CAPTURE) para ganarle al <a> del card
  document.addEventListener(
    "click",
    (e) => {
      const prev = e.target.closest("[data-ct-pc-prev]");
      const next = e.target.closest("[data-ct-pc-next]");
      if (!prev && !next) return;

      const root = e.target.closest("[data-ct-pc-carousel]");
      if (!root) return;

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();

      const index = getIndex(root);
      if (prev) goTo(root, index - 1);
      if (next) goTo(root, index + 1);
    },
    true
  );

  // Swipe básico (delegado)
  const startX = new WeakMap();

  document.addEventListener(
    "pointerdown",
    (e) => {
      const root = e.target.closest("[data-ct-pc-carousel]");
      if (!root) return;
      startX.set(root, e.clientX);
    },
    true
  );

  document.addEventListener(
    "pointerup",
    (e) => {
      const root = e.target.closest("[data-ct-pc-carousel]");
      if (!root) return;

      const sx = startX.get(root);
      if (typeof sx !== "number") return;

      const diff = e.clientX - sx;
      startX.delete(root);

      if (Math.abs(diff) < 30) return;

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();

      const index = getIndex(root);
      if (diff < 0) goTo(root, index + 1);
      else goTo(root, index - 1);
    },
    true
  );

  // Inicial: set index 0 a todos
  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("[data-ct-pc-carousel]").forEach((root) => {
      if (!root.hasAttribute("data-ct-pc-index")) setIndex(root, 0);
    });
  });
})();

/* ============================================
   COMPACT HEADER ON SCROLL - Calcetubos V3 (FIXED)
   ============================================ */
/*(function () {
  console.log("🚀 Iniciando Compact Header V3...");

  const SCROLL_THRESHOLD = 250;
  const EXIT_THRESHOLD = 120;
  const DESKTOP = window.matchMedia("(min-width: 769px)");

  const storeHeader = document.querySelector("store-header");
  if (!storeHeader) {
    console.log("❌ No se encontró store-header");
    return;
  }

  // Spacer: evita salto cuando el header pasa a position:fixed
  let ctSpacer = document.getElementById("ct-header-spacer");
  if (!ctSpacer) {
    ctSpacer = document.createElement("div");
    ctSpacer.id = "ct-header-spacer";
    ctSpacer.style.height = "0px";
    storeHeader.parentNode.insertBefore(ctSpacer, storeHeader);
  }

  // Guardar estado original del menú normal (desktop)
  const details = storeHeader.querySelector("details.main-menu__disclosure");
  const DEFAULT_OPEN = !!(details && details.hasAttribute("open"));
  const DEFAULT_IS_OPEN = !!(details && details.classList.contains("is-open"));

  let ctIsCompact = storeHeader.classList.contains("is-compact");

  function setAnnouncementVar() {
    const ann =
      document.querySelector("announcement-bar") ||
      document.querySelector(".announcement-bar") ||
      document.querySelector('[class*="announcement"]');

    const annH = ann ? ann.offsetHeight : 0;
    document.documentElement.style.setProperty("--ct-ann-h", annH + "px");
  }
  setAnnouncementVar();
  window.addEventListener("resize", setAnnouncementVar);

  function resetToNormalHeaderDesktop() {
    if (!DESKTOP.matches) return;

    const detailsNow = storeHeader.querySelector("details.main-menu__disclosure");
    if (!detailsNow) return;

    if (DEFAULT_OPEN) detailsNow.setAttribute("open", "");
    else detailsNow.removeAttribute("open");

    detailsNow.classList.toggle("is-open", DEFAULT_IS_OPEN);

    const summary = detailsNow.querySelector("summary.main-menu__toggle");
    if (summary) summary.setAttribute("aria-expanded", DEFAULT_OPEN ? "true" : "false");
  }

  function updateHeader() {
  const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;

  // ✅ Estado deseado con hysteresis (zona muerta entre EXIT y SCROLL)
  // - arriba de SCROLL => compacto
  // - abajo de EXIT => normal
  // - entre ambos => mantener estado actual (evita parpadeo en scroll lento)
  const shouldBeCompact =
    scrollY > SCROLL_THRESHOLD ? true :
    scrollY < EXIT_THRESHOLD ? false :
    ctIsCompact;

  // ✅ Si no cambia el estado, no tocamos DOM (cero repaints innecesarios)
  if (shouldBeCompact === ctIsCompact) return;

  // Entrar a compacto
  if (shouldBeCompact) {
    storeHeader.classList.add("is-compact");
    

    // ✅ Cerrar menú al entrar a compacto (evita que aparezca abierto)
    const d = storeHeader.querySelector("details.main-menu__disclosure");
    if (d) {
      d.classList.remove("is-open");
      d.removeAttribute("open");
      d.open = false;
      const s = d.querySelector("summary.main-menu__toggle");
      if (s) s.setAttribute("aria-expanded", "false");
    }

    ctSpacer.style.height = storeHeader.offsetHeight + "px";
    ctIsCompact = true;
    return;
  }

  // Salir de compacto
  storeHeader.classList.remove("is-compact");
  
  ctSpacer.style.height = "0px";
  ctIsCompact = false;
  resetToNormalHeaderDesktop();
}

  let ticking = false;
  window.addEventListener(
    "scroll",
    function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateHeader();
        ticking = false;
      });
    },
    { passive: true }
  );

  updateHeader();
  window.addEventListener("pageshow", () => requestAnimationFrame(updateHeader));

  console.log("✅ Compact header V3 ready");
})();*/

/* ============================================
   CT – Desktop hamburger => toggle drawer en compacto
   (solo desktop, mobile lo maneja Canopy)
   ============================================ */
(function () {
  const DESKTOP = window.matchMedia("(min-width: 769px)");

  function getCompactHeader() {
    const sh = document.querySelector("store-header");
    if (!sh) return null;
    if (!sh.classList.contains("is-compact")) return null;
    return sh;
  }
  
  function closeMenu(details) {
  details.classList.remove("is-open");
  details.removeAttribute("open");
  details.open = false;
  const summary = details.querySelector("summary.main-menu__toggle");
  if (summary) summary.setAttribute("aria-expanded", "false");
}

  function openMenu(details) {
  details.classList.add("is-open");
  details.setAttribute("open", "");
  details.open = true;
  const summary = details.querySelector("summary.main-menu__toggle");
  if (summary) summary.setAttribute("aria-expanded", "true");
}

  document.addEventListener("click", function (e) {
    if (!DESKTOP.matches) return;

    const storeHeader = getCompactHeader();
    if (!storeHeader) return;

    const summary = e.target.closest("store-header.is-compact summary.main-menu__toggle");
    if (!summary) {
      // click fuera => si está abierto, cerrar (backdrop “lógico”)
      const openDetails = storeHeader.querySelector("details.main-menu__disclosure[open], details.main-menu__disclosure.is-open");
      if (!openDetails) return;

      const panel = openDetails.querySelector(".main-menu__content");
      const clickedInsidePanel = panel && panel.contains(e.target);
      const clickedInsideToggle = !!e.target.closest("store-header.is-compact summary.main-menu__toggle");
      if (!clickedInsidePanel && !clickedInsideToggle) closeMenu(openDetails);

      return;
    }

    const details = summary.closest("details.main-menu__disclosure");
    if (!details) return;

    // Importantísimo: en desktop el tema puede “interceptar” el summary
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();

    if (details.classList.contains("is-open") || details.hasAttribute("open")) {
      closeMenu(details);
    } else {
      openMenu(details);
    }
  }, true);

  document.addEventListener("keydown", function (e) {
    if (!DESKTOP.matches) return;
    if (e.key !== "Escape") return;

    const storeHeader = getCompactHeader();
    if (!storeHeader) return;

    const openDetails = storeHeader.querySelector("details.main-menu__disclosure[open], details.main-menu__disclosure.is-open");
    if (openDetails) closeMenu(openDetails);
  });
})();
/* ============================================
   CT – Close menu ONLY when compact becomes active (desktop)
   (evita que se cierre después de tu click)
   ============================================ */
/*(function () {
  const DESKTOP = window.matchMedia("(min-width: 769px)");

  function getHeader() {
    return document.querySelector("store-header");
  }

  function closeMenu(sh) {
    const details = sh?.querySelector("details.main-menu__disclosure");
    if (!details) return;

    details.classList.remove("is-open");
    details.removeAttribute("open");

    const summary = details.querySelector("summary.main-menu__toggle");
    if (summary) summary.setAttribute("aria-expanded", "false");
  }

  function setup() {
    const sh = getHeader();
    if (!sh) return;

    // Estado previo: para detectar transición "no compact" -> "compact"
    let wasCompact = sh.classList.contains("is-compact");

    // Si ya carga en compacto por alguna razón, cerramos UNA vez.
    if (DESKTOP.matches && wasCompact) closeMenu(sh);

    const obs = new MutationObserver(() => {
      const isCompactNow = sh.classList.contains("is-compact");

      // Solo cuando ACABA de entrar a compacto
      if (DESKTOP.matches && isCompactNow && !wasCompact) {
        closeMenu(sh);
      }

      wasCompact = isCompactNow;
    });

    obs.observe(sh, { attributes: true, attributeFilter: ["class"] });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", setup, { once: true });
  } else {
    setup();
  }
})();*/
/* ============================================
   CT – Desktop: devolver clicks a logo/lupa/usuario/carrito (compact)
   ============================================ */
/*(function () {
  const DESKTOP = window.matchMedia("(min-width: 769px)");

  function run() {
    if (!DESKTOP.matches) return;

    const sh = document.querySelector("store-header.is-compact");
    if (!sh) return;

    // 1) Quitar clase que el tema usa para interceptar clicks
    const icons = sh.querySelector(".header__icons");
    if (icons && icons.classList.contains("js-closes-menu")) {
      icons.classList.remove("js-closes-menu");
    }

    // 2) Si el details/panel está CERRADO, no debe capturar clicks
    const details = sh.querySelector("details.main-menu__disclosure");
    const panel = details?.querySelector(".main-menu__content");

    if (details && !details.hasAttribute("open") && !details.classList.contains("is-open")) {
      details.style.pointerEvents = "none";
      const summary = details.querySelector("summary.main-menu__toggle");
      if (summary) summary.style.pointerEvents = "auto";
      if (panel) panel.style.pointerEvents = "none";
    } else if (details) {
      details.style.pointerEvents = "auto";
      if (panel) panel.style.pointerEvents = "auto";
    }

    // 3) Asegurar que links/botones del header sí reciban click
    sh.querySelectorAll(".header__icons a, .header__icons button, .header__logo a").forEach(el => {
      el.style.pointerEvents = "auto";
    });
  }

  // correr al cargar
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run, { once: true });
  } else {
    run();
  }

  // correr al cambiar scroll/estado (porque el header entra/sale de compacto)
  window.addEventListener("resize", run);

  // y también observar cambios de clase
  const shAny = document.querySelector("store-header");
  if (shAny) {
    const obs = new MutationObserver(run);
    obs.observe(shAny, { attributes: true, attributeFilter: ["class"] });
  }
})();*/ 
/* ============================================
   CT – Desktop COMPACT: asegurar que logo/iconos naveguen
   (Canopy a veces intercepta clicks y los cancela)
   ============================================ */
/*(function () {
  const DESKTOP = window.matchMedia("(min-width: 769px)");

  function isCompact() {
    const sh = document.querySelector("store-header");
    return sh && sh.classList.contains("is-compact");
  }

  document.addEventListener(
    "click",
    function (e) {
      if (!DESKTOP.matches) return;
      if (!isCompact()) return;

      // Si es el hamburger, NO tocamos nada (lo maneja tu script)
      if (e.target.closest("summary.main-menu__toggle")) return;

      // Cualquier link clickeado dentro del logo o de los iconos (carrito/lupa/usuario)
      const link =
        e.target.closest("store-header.is-compact .header__logo a") ||
        e.target.closest("store-header.is-compact .header__icons a");

      if (!link) return;

      const href = link.getAttribute("href");
      if (!href || href === "#" || href.startsWith("javascript:")) return;

      // Forzamos navegación real (evita que otro JS haga preventDefault)
      e.preventDefault();
      e.stopPropagation();
      window.location.href = href;
    },
    true // capture para ganarle a scripts del tema
  );
})();*/
/* CT – Mobile: toggle drawer en compacto (evita salto arriba) */
(function () {
  const MOBILE = window.matchMedia("(max-width: 768px)");

  document.addEventListener("click", function (e) {
    if (!MOBILE.matches) return;

    const sh = document.querySelector("store-header.is-compact");
    if (!sh) return;

    const summary = e.target.closest("store-header.is-compact summary.main-menu__toggle");
    if (!summary) return;

    const details = summary.closest("details.main-menu__disclosure");
    if (!details) return;

    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();

    details.open = !details.open;
    details.classList.toggle("is-open", details.open);
    summary.setAttribute("aria-expanded", details.open ? "true" : "false");
  }, true);
})();
/* ============================================
   CT – Compact header (NO BRINCO) + Ann var + Close menu
   ============================================ */
(() => {
  const SCROLL_IN = 250;
  const SCROLL_OUT = 120;
  const COMPACT_H = 55;

  const sh = document.querySelector("store-header");
  if (!sh) return;

const DESKTOP = window.matchMedia("(min-width: 769px)");
let menuWasOpen = false;
  // 1) Mantener actualizada la variable de altura del announcement (midiendo el nodo con altura REAL)
const setAnnVar = () => {
  // Prioridad: el elemento que normalmente tiene la altura real
  const candidates = [
    document.querySelector(".cc-announcement .announcement-bar"),
    document.querySelector(".announcement-bar"),
    document.querySelector("announcement-bar"),
    document.querySelector(".cc-announcement"),
    document.querySelector('[class*="announcement"]')
  ].filter(Boolean);

  let h = 0;
  for (const el of candidates) {
    const rh = Math.ceil(el.getBoundingClientRect().height);
    if (rh > h) h = rh;
  }

  document.documentElement.style.setProperty("--ct-ann-h", `${h}px`);
};

// Corre una vez y luego SOLO cuando realmente cambie el announcement
setAnnVar();

window.addEventListener("resize", () => requestAnimationFrame(setAnnVar));

const getAnnEl = () =>
  document.querySelector(".cc-announcement .announcement-bar") ||
  document.querySelector(".announcement-bar") ||
  document.querySelector("announcement-bar") ||
  document.querySelector(".cc-announcement") ||
  document.querySelector('[class*="announcement"]');

let annRO = null;
const attachAnnObserver = () => {
  const el = getAnnEl();
  if (!el || !("ResizeObserver" in window)) return;
  if (annRO) annRO.disconnect();
  annRO = new ResizeObserver(() => requestAnimationFrame(setAnnVar));
  annRO.observe(el);
};

attachAnnObserver();
window.addEventListener("load", attachAnnObserver);

  // 2) Spacer (reserva el alto NORMAL para que no cambie el layout al pasar a fixed)
  let sp = document.getElementById("ct-header-spacer");
  if (!sp) {
    sp = document.createElement("div");
    sp.id = "ct-header-spacer";
    sp.style.height = "0px";
    sh.parentNode.insertBefore(sp, sh);
  }

  const closeMenu = () => {
  const d = sh.querySelector("details.main-menu__disclosure");
  if (!d) return;

  // ✅ guardar cómo estaba ANTES de cerrarlo
  if (DESKTOP.matches) {
    menuWasOpen = d.open || d.hasAttribute("open") || d.classList.contains("is-open");
  }

  // cerrar (lo que ya hacías)
  d.classList.remove("is-open");
  d.removeAttribute("open");
  d.open = false;

  const s = d.querySelector("summary.main-menu__toggle");
  if (s) s.setAttribute("aria-expanded", "false");
};

  let isCompact = sh.classList.contains("is-compact");
  let lastNormalH = Math.ceil(sh.getBoundingClientRect().height) || 0;

  const setCompact = () => {
  const normalH = lastNormalH || Math.ceil(sh.getBoundingClientRect().height);

  // asegura --ct-ann-h antes del primer frame compacto (lo mantienes)
  setAnnVar();

  // ✅ clave: reserva espacio EN ESTE MISMO FRAME
  sp.style.height = `${normalH}px`;

  // ahora sí: entra a compacto (fixed)
  sh.classList.add("is-compact");
  closeMenu();

  isCompact = true;
};

const unsetCompact = () => {
  // salir de compacto
  sh.classList.remove("is-compact");
  sp.style.height = "0px";
  isCompact = false;

  // ✅ restaurar franja/menú normal si estaba abierto antes de compactar
  if (DESKTOP.matches && menuWasOpen) {
    const d = sh.querySelector("details.main-menu__disclosure");
    if (d) {
      d.classList.add("is-open");
      d.setAttribute("open", "");
      d.open = true;
      const s = d.querySelector("summary.main-menu__toggle");
      if (s) s.setAttribute("aria-expanded", "true");
    }
    menuWasOpen = false; // limpia para el siguiente ciclo
  }
};

  const update = () => {
    const y = window.scrollY;

    // Mientras NO estamos compactos, guardamos el alto real del header normal
    if (!isCompact) {
      const h = Math.ceil(sh.getBoundingClientRect().height);
      if (h > 60) lastNormalH = h; // evita guardar el 55 del compacto
    }

    const should =
      y > SCROLL_IN ? true :
      y < SCROLL_OUT ? false :
      isCompact;

    if (should === isCompact) return;
    should ? setCompact() : unsetCompact();
  };

  let ticking = false;
  window.addEventListener("scroll", () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      update();
      ticking = false;
    });
  }, { passive: true });

  window.addEventListener("pageshow", () => requestAnimationFrame(update));
  update();
})();
console.log("✅ CT custom.js LOADED");